import React, { useState, useCallback } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { MarkdownWYSIWYGEditor } from '@/components/common/MarkdownWYSIWYGEditor';
import { useReextractSalesOrderDraft } from '@/api/accounts-receivable/sales-orders/useReextractSalesOrderDraft';
import { SaveNoteToMemoryBankDialog } from './SaveNoteToMemoryBankDialog';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import {
  MemoryBankNoteMetadata,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import { useInvalidateRpcQueryFn } from '@/api/rpc/client';

interface ReextractSalesOrderDraftDialogProps {
  inboundEmailId: string;
  classificationId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  metadata?: MemoryBankNoteMetadata;
  entityId: string;
  entityType: MemoryBankSupportedEntityTypes;
}

export function ReextractSalesOrderDraftDialog({
  inboundEmailId,
  classificationId,
  open,
  onOpenChange,
  onSuccess,
  metadata,
  entityId,
  entityType,
}: ReextractSalesOrderDraftDialogProps) {
  const gt = useGT();
  const [refreshOCR, setRefreshOCR] = useState(true);
  const [userNote, setUserNote] = useState('');
  const [lastResult, setLastResult] = useState<{
    success: boolean;
    error?: string;
  } | null>(null);
  const invalidateUsagesQuery = useInvalidateRpcQueryFn(
    (orpc) => [
      orpc.memoryBank.listUsages.key({ input: { entityId, entityType } }),
    ],
    [entityId, entityType],
  );

  const {
    reextract,
    isReextracting,
    error,
    showSaveNoteDialog,
    lastUserNote,
    dismissSaveNoteDialog,
  } = useReextractSalesOrderDraft({
    onSuccess: (result) => {
      setLastResult({
        success: result.success,
        error: result.error,
      });

      if (result.success) {
        invalidateUsagesQuery();
        onSuccess?.();
        // Close the dialog after showing success for a moment, but only if no note dialog is shown
        if (!result.userContextNote?.trim()) {
          setTimeout(() => {
            handleClose();
          }, 2000);
        }
      }
    },
    onError: (error) => {
      setLastResult({
        success: false,
        error: error.message,
      });
    },
  });

  const handleReextract = useCallback(async () => {
    setLastResult(null);

    try {
      await reextract({
        inboundEmailId,
        classificationId,
        userContextNoteMarkdown: userNote.trim() || undefined,
        refreshOCR,
      });
    } catch (err) {
      // Error is handled by the hook
    }
  }, [reextract, inboundEmailId, classificationId, userNote, refreshOCR]);

  const handleClose = useCallback(() => {
    if (!isReextracting) {
      onOpenChange(false);
      // Reset state after a delay to allow dialog animation
      setTimeout(() => {
        setUserNote('');
        setLastResult(null);
      }, 200);
    }
  }, [isReextracting, onOpenChange]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowsClockwiseIcon className="h-5 w-5" />
              <T context="Dialog title for re-extracting a purchase order using AI">
                Re-extract Purchase Order
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="Description for the re-extract purchase order dialog">
                Re-run the AI extraction process for this purchase order with
                optional context.
              </T>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* OCR Refresh Option */}
            <div className="flex items-center space-x-2">
              <Switch
                id="refresh-ocr"
                checked={refreshOCR}
                onCheckedChange={setRefreshOCR}
                disabled={isReextracting}
              />
              <Label htmlFor="refresh-ocr" className="text-sm">
                <T context="Label for switch that controls whether to redo OCR text extraction on document">
                  Redo document text extraction
                </T>
              </Label>
            </div>

            <p className="text-sm text-muted-foreground">
              {refreshOCR ? (
                <T context="Description when OCR refresh is enabled: will redo attachment text extraction before AI matching">
                  Will re-process attachments text extraction, and then rerun AI
                  matching.
                </T>
              ) : (
                <T context="Description when OCR refresh is disabled: uses existing text extraction and only reruns AI matching">
                  Will use existing attachment text extraction and only rerun AI
                  matching.
                </T>
              )}
            </p>

            {/* User Context Note Section */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                <T context="Label for optional markdown context note field in re-extraction dialog">
                  Optional Context Note (Markdown)
                </T>
              </Label>
              <p className="text-sm text-muted-foreground">
                <T context="Helper text explaining the purpose of the context note for AI extraction">
                  Provide additional context to help the AI with extraction
                  accuracy.
                </T>
              </p>

              <div className="space-y-2">
                <MarkdownWYSIWYGEditor
                  initialValue={userNote}
                  onChange={setUserNote}
                  placeholder={gt(
                    'Provide context to help with extraction (e.g., customer naming conventions, product details, etc.). Or explain what went wrong with the previous extraction.',
                    {
                      context:
                        'placeholder text for the context note editor in the re-extraction dialog',
                    },
                  )}
                  height={150}
                  showToolbar
                />
              </div>
            </div>

            {/* Result Display */}
            {lastResult && (
              <AlertContainer
                variant={lastResult.success ? 'default' : 'destructive'}
              >
                <AlertTitle>
                  {lastResult.success ? (
                    <T context="Alert title when re-extraction succeeded">
                      Success
                    </T>
                  ) : (
                    <T context="Alert title when re-extraction failed">Error</T>
                  )}
                </AlertTitle>
                <AlertDescription>
                  {lastResult.success ? (
                    <T context="Success message after purchase order was re-extracted by AI">
                      Purchase order re-extracted successfully!
                    </T>
                  ) : (
                    <T context="Error message when re-extraction failed, includes the error details">
                      Re-extraction failed: <Var>{lastResult.error}</Var>
                    </T>
                  )}
                </AlertDescription>
              </AlertContainer>
            )}

            {/* Error Display */}
            {error && !lastResult && (
              <AlertContainer variant="destructive">
                <AlertTitle>
                  <T context="Alert title for unexpected error during re-extraction">
                    Error
                  </T>
                </AlertTitle>
                <AlertDescription>{error.message}</AlertDescription>
              </AlertContainer>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isReextracting}
            >
              {lastResult?.success ? (
                <T context="Button label to close dialog after successful re-extraction">
                  Close
                </T>
              ) : (
                <T context="Button label to cancel and close the re-extraction dialog">
                  Cancel
                </T>
              )}
            </Button>
            <Button onClick={handleReextract} disabled={isReextracting}>
              {isReextracting ? (
                <>
                  <ArrowsClockwiseIcon className="h-4 w-4 mr-2 animate-spin" />
                  <T context="Button label while re-extraction is in progress">
                    Re-extracting...
                  </T>
                </>
              ) : (
                <T context="Button label to start the re-extraction process">
                  Re-extract
                </T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Note to Memory Bank Dialog */}
      <SaveNoteToMemoryBankDialog
        open={showSaveNoteDialog}
        onOpenChange={() => {}} // Controlled by hook
        note={lastUserNote}
        metadata={metadata}
        entityId={entityId}
        entityType={entityType}
        onSaveNoteToMemoryBank={() => {
          dismissSaveNoteDialog();
          // Close the main dialog after saving note
          handleClose();
        }}
        onSkipSaving={() => {
          dismissSaveNoteDialog();
          // Close the main dialog after skipping note save
          handleClose();
        }}
      />
    </>
  );
}
